/**
 * WorkOrderDetailPage - Full detail view for a single work order.
 *
 * Displays all work order information organized into tabbed sections:
 * - Details: all WO fields (project info, location, contact, settings)
 * - Photos: before/after photo grids with upload via PhotoGrid component
 * - Billing: financial information (hidden if user lacks canViewFinancials permission)
 *
 * Note: The legacy Time Logs tab was retired in favor of structured hour
 * allocation (per-assignee hours for T&M jobs, project-hour allocation for
 * Project Work Orders). The work_order_time_logs table and API endpoints
 * remain in place to preserve historical records.
 *
 * All actions live in a single DetailActionBar (workflow re-entry, status
 * transitions from utils/statusTransitions, Edit, Generate PDF, and an overflow
 * kebab for Duplicate / Create Follow-Up / Cancel), so a phone shows one
 * hierarchy of buttons rather than a header cluster stacked on a status bar.
 *
 * Data is fetched from GET /work-orders/:id on mount.
 * Status transitions call PATCH /work-orders/:id with the new status.
 *
 * @route /work-orders/:id
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteJob } from '../../hooks/useDeleteJob';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import { useNotifyPrompt } from '../../hooks/useNotifyPrompt';
import { useDiscardGuard } from '../../hooks/useDiscardGuard';
import { workOrderService } from '../../services/workOrderService';
import { qk } from '../../lib/queryKeys';
import { invalidateJobCaches } from '../../lib/invalidateJobCaches';
import { checklistInstanceService } from '../../services/checklistInstanceService';
import type { ChecklistInstance } from '../../services/checklistInstanceService';
import { checklistService } from '../../services/checklistService';
import { contactService } from '../../services/contactService';
import { Alert, EstimateBadge, EventTypeBadge, BackLink, Icon } from '../../components/atoms';
import { isProjectBusinessLine } from '../../services/scheduledEventsService';
import { EstimateApprovalControl, HoursApprovalControl } from '../../components/organisms';
import { DetailActionBar, RecipientList, reachableRecipients } from '../../components/molecules';
import type { DetailAction } from '../../components/molecules';
import { ConfirmModal } from '../../components/organisms';
import type { EstimateApprovalContactOption } from '../../components/organisms';
import { photoTypeService } from '../../services/photoTypeService';
import { camelizeKeys } from '../../services/api';
import { uploadService } from '../../services/uploadService';
import type { WorkOrder, WorkOrderPhoto } from '../../services/workOrderService';
import { normalizeWorkOrder } from '../../services/workOrderService';
import { PhotoGrid } from '../../components/organisms/PhotoGrid';
import { getStatusTransitions } from '../../utils/statusTransitions';
import { SignatureCapture } from '../../components/organisms/SignatureCapture';
import { Spinner } from '../../components/atoms/Spinner';
import Modal from '../../components/organisms/Modal/Modal';
import { JobForm } from '../../components/organisms/JobForm/JobForm';
import { WorkOrderWorkflow } from '../../components/organisms/WorkOrderWorkflow';
import type { CapturedPhoto } from '../../components/organisms/PhotoCapture';
import { capturedPhotoToFile } from '../../components/organisms/PhotoCapture';
import { usePhotoUploadQueue } from '../../hooks/usePhotoUploadQueue';
import { STORE_NAMES } from '../../services/offlineStorage';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { formatLabel, formatPersonName } from '../../utils/formatLabel';
import { formatCurrency } from '../../utils/formatCurrency';
import { generateWorkOrderPdf } from '../../utils/pdfGenerator';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Tab identifiers for the detail page sections */
type DetailTab = 'details' | 'photos' | 'billing';

/** Status badge color classes (same as list page for consistency) */
const STATUS_COLORS: Record<string, string> = {
	draft: 'bg-gray-100 text-gray-800 border-gray-300',
	submitted: 'bg-yellow-100 text-yellow-800 border-yellow-300',
	approved: 'bg-blue-100 text-blue-800 border-blue-300',
	in_progress: 'bg-indigo-100 text-indigo-800 border-indigo-300',
	on_hold: 'bg-orange-100 text-orange-800 border-orange-300',
	completed: 'bg-green-100 text-green-800 border-green-300',
	invoiced: 'bg-purple-100 text-purple-800 border-purple-300',
	closed: 'bg-gray-100 text-gray-600 border-gray-300',
	cancelled: 'bg-red-100 text-red-800 border-red-300',
};

/** Business line badge color classes */
const BUSINESS_LINE_COLORS: Record<string, string> = {
	janitorial: 'bg-teal-100 text-teal-800 border-teal-300',
	construction: 'bg-amber-100 text-amber-800 border-amber-300',
	maintenance: 'bg-sky-100 text-sky-800 border-sky-300',
	landscaping: 'bg-emerald-100 text-emerald-800 border-emerald-300',
	snow_removal: 'bg-slate-100 text-slate-800 border-slate-300',
	security: 'bg-violet-100 text-violet-800 border-violet-300',
};

/* Category accent for the page header. Mirrors CalendarView.CATEGORY_COLORS
 * so a project WO chip clicked on the calendar lands on a page whose
 * accent matches the chip color (purple for construction, brand orange
 * otherwise). */
const CATEGORY_ACCENT_COLOR: Record<'project_work_order' | 'work_order', string> = {
	project_work_order: '#6D28D9',
	work_order: 'var(--brand-primary, #F9550E)',
};

const WorkOrderDetailPage: React.FC = () => {
	/* Extract the work order ID from the URL params */
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { user, hasAnyRole } = useAuth();

	/* "Notify users" gate for the status buttons. Approving or completing a
	 * work order emails the owner and everyone watching this customer — an
	 * admin sees that list, and can trim it, before the transition commits. */
	const { requestNotifyDecision, notifyPrompt } = useNotifyPrompt({ lead: 'This status change will email' });

	/* Auto-open edit modal when navigating to /work-orders/:id/edit */
	const isEditRoute = location.pathname.endsWith('/edit');

	/* Auto-open the workflow overlay when ?workflow=1 is on the URL.
	 * Used by the Dashboard's "Start Work" / "Resume Task" buttons to
	 * jump straight into the checklist without an intermediate stop on
	 * the detail page. */
	const wantsWorkflow = new URLSearchParams(location.search).get('workflow') === '1';

	const queryClient = useQueryClient();

	/* --- UI state --- */
	const [activeTab, setActiveTab] = useState<DetailTab>('details');
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	/** Controls visibility of the edit work order modal */
	const [showEditModal, setShowEditModal] = useState(isEditRoute);
	/* The edit form's unsaved edits, so closing the modal by accident —
	   Escape, a click outside, the X — asks before throwing them away. */
	const [editFormDirty, setEditFormDirty] = useState(false);
	const closeEditModal = () => {
		setShowEditModal(false);
		setEditFormDirty(false);
	};
	const { requestClose: requestCloseEditModal, discardPrompt: discardEditPrompt } = useDiscardGuard({ isDirty: editFormDirty, onClose: closeEditModal });
	/** Controls visibility of the full-screen workflow overlay.
	 *  Initialized true when the URL carries ?workflow=1 so deep-links
	 *  from the Dashboard land directly on the checklist. */
	const [showWorkflow, setShowWorkflow] = useState(wantsWorkflow);

	/* "Email Contact to Verify" — the reviewer's follow-up to the technician's
	   pre-submit prompt. When the tech answered "Don't Email", this is how the
	   customer gets asked once someone has reviewed the work; it's also the
	   resend when the first request went astray. Same confirm-who-gets-it
	   pattern as the field prompt, so nothing leaves without being read first.
	     showVerifyPrompt  — the confirmation dialog
	     verifyRequestSending / verifyRequestError — request state, with the
	       error kept inside the dialog rather than behind it
	     verifyRequestSentTo — address of the last successful send, shown as a
	       confirmation banner until the page is left */
	const [showVerifyPrompt, setShowVerifyPrompt] = useState(false);
	const [verifyRequestSending, setVerifyRequestSending] = useState(false);
	const [verifyRequestError, setVerifyRequestError] = useState<string | null>(null);
	const [verifyRequestSentTo, setVerifyRequestSentTo] = useState<string | null>(null);

	/** Contacts on the WO's company — used by EstimateApprovalControl
	 *  to populate the customer-approval picker. Loaded lazily after the
	 *  work order itself resolves so we know which company to query. */
	const [companyContacts, setCompanyContacts] = useState<EstimateApprovalContactOption[]>([]);

	/* Network connectivity state — used to decide between online upload vs offline queue */
	const { isOnline, addPendingItem } = useOfflineSync();

	/* Background photo uploads. Capturing a photo returns control to the tech
	   immediately; the bytes go up behind the scenes and the grid renders a
	   live status chip per photo. `meta.photoType` keeps each in-flight tile
	   in the right grid (Before vs After). */
	const photoUploads = usePhotoUploadQueue<{ photoType: 'before' | 'after' }>();

	/* Role-based visibility flags — super_admin inherits admin privileges */
	const canEdit = hasAnyRole(['super_admin', 'admin', 'manager']);
	/* Delete lives behind the kebab (managers and above, matching the
	   server's gate); after it goes there is nothing to show, so return
	   to the list. */
	const { requestDelete, deleteModal } = useDeleteJob('work_order', { onDeleted: () => navigate('/work-orders') });
	const canViewFinancials = user?.canViewFinancials ?? false;

	/**
	 * Work order read (TanStack Query). isLoading is true only on the first
	 * load (no cached data); background refetches after mutations update
	 * silently, so overlays like the workflow aren't unmounted — this
	 * replaces the old hasLoadedRef spinner-gating.
	 */
	const workOrderQuery = useQuery({
		queryKey: qk.workOrders.detail(id ?? ''),
		enabled: !!id,
		queryFn: async (): Promise<WorkOrder> => {
			const response = await workOrderService.getById(id as string);
			/* Backend wraps single resources in { data: ... } and uses snake_case keys.
			 * Unwrap, camelize, and flatten nested relations into the WorkOrder interface. */
			const raw = (response.data as any)?.data ?? response.data;
			const camelized = camelizeKeys(raw) as Record<string, unknown>;
			return normalizeWorkOrder(camelized);
		},
	});

	const workOrder: WorkOrder | null = workOrderQuery.data ?? null;
	const isLoading = !!id && workOrderQuery.isLoading;
	const error = workOrderQuery.error ? (workOrderQuery.error as Error).message : null;

	/**
	 * Refetch the work order. Named `fetchWorkOrder` for the many call sites
	 * (status change, photo add/delete, signature, edit-modal onChange) that
	 * refresh the record after a mutation. Also invalidates the WO lists plus
	 * the Dashboard reads so those surfaces reflect the change when the user
	 * navigates back — a status change here moves Dashboard counts and the
	 * day's task feed. `listsOnly` because the record itself is refetched
	 * directly above; invalidating its detail key too would double-fetch it.
	 */
	const fetchWorkOrder = useCallback(async () => {
		await workOrderQuery.refetch();
		invalidateJobCaches(queryClient, 'work_order', { listsOnly: true });
	}, [workOrderQuery, queryClient]);

	/**
	 * Polymorphic checklist instance attached to this WO (null when no template
	 * has been assigned). Cached via TanStack; re-fetched after the edit modal
	 * closes (saving the form may have changed the assignment).
	 */
	const checklistInstanceQuery = useQuery({
		queryKey: qk.checklistInstances.detail(`wo-${id ?? ''}`),
		enabled: !!id,
		queryFn: async (): Promise<ChecklistInstance | null> => {
			try {
				const res = await checklistInstanceService.getForWorkOrder(id as string);
				return ((res.data as any)?.data ?? null) as ChecklistInstance | null;
			} catch {
				/* Non-fatal — surface absence as null */
				return null;
			}
		},
	});
	const checklistInstance: ChecklistInstance | null = checklistInstanceQuery.data ?? null;
	const fetchChecklistInstance = useCallback(async () => {
		await checklistInstanceQuery.refetch();
	}, [checklistInstanceQuery]);

	/**
	 * Load the company's contacts once we know the WO's company. Used
	 * by EstimateApprovalControl to power the "Customer Approval"
	 * picker. Re-runs when the company changes (rare — only via edit).
	 */
	useEffect(() => {
		const companyId = workOrder?.companyId;
		if (!companyId) {
			setCompanyContacts([]);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const res = await contactService.getByCompany(String(companyId));
				if (cancelled) return;
				const rows = ((res.data as any)?.data ?? res.data ?? []) as Array<any>;
				setCompanyContacts(
					rows.map((c) => ({
						id: Number(c.id),
						name: `${c.firstName ?? c.first_name ?? ''} ${c.lastName ?? c.last_name ?? ''}`.trim() || `Contact #${c.id}`,
					}))
				);
			} catch {
				/* Non-fatal — picker will show empty-state */
				if (!cancelled) setCompanyContacts([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [workOrder?.companyId]);

	/**
	 * Handles a status transition by PATCHing the new status to the API.
	 * Re-fetches the work order after a successful update to reflect changes.
	 *
	 * Special case: when transitioning to "in_progress" (Start Work / Resume
	 * Work), the workflow overlay opens automatically so the technician can
	 * begin the checklist immediately. A reviewer's "Request Changes"
	 * (in_review -> in_progress) also lands on in_progress but is a
	 * send-back, not a pick-up — the reviewer has no use for the tech's
	 * checklist, so the overlay stays closed for that one.
	 */
	const handleStatusTransition = async (newStatus: string) => {
		if (!id) return;

		/* Ask who this transition would email before anything moves. First,
		   so cancelling at the prompt leaves the work order exactly where it
		   was — and before the overlay opens below, so a cancelled Start
		   doesn't strand the tech in a workflow for a status that never
		   changed. Transitions that email nobody (Start, On Hold) never
		   prompt. */
		const notifyDecision = await requestNotifyDecision({ kind: 'work_order', id, status: newStatus });
		if (!notifyDecision) return;

		// When starting work, open the workflow overlay immediately so it's
		// visible as soon as the status update completes and the page re-renders.
		// Setting this before fetchWorkOrder avoids a race condition where the
		// re-render from loading state would reset the flag.
		const opensWorkflow = newStatus === 'in_progress' && workOrder?.status !== 'in_review';
		if (opensWorkflow) {
			setShowWorkflow(true);
		}

		try {
			await workOrderService.update(id, { status: newStatus as WorkOrder['status'], ...notifyDecision });
			await fetchWorkOrder();
		} catch (err) {
			// Revert the workflow overlay if the status transition failed
			if (opensWorkflow) {
				setShowWorkflow(false);
			}
			alert(err instanceof Error ? err.message : 'Failed to update status');
		}
	};

	/**
	 * Email the on-site contact asking them to verify and sign.
	 *
	 * The other half of the technician's "Don't Email" answer: the customer is
	 * asked once someone has actually reviewed the work. Doubles as a resend.
	 * On failure the dialog stays open with the message inside it — the point
	 * of the dialog is to be sure about who was contacted, so "did that send?"
	 * has to be answered in the same place it was asked.
	 */
	const handleRequestVerification = async () => {
		if (!id || verifyRequestSending) return;
		setVerifyRequestSending(true);
		setVerifyRequestError(null);
		try {
			const result = await checklistService.requestContactVerification(id);
			setVerifyRequestSentTo(result.contactEmail ?? null);
			setShowVerifyPrompt(false);
			/* Refetch so the "Verification Requested" stamp and the button's
			   first-ask/resend label reflect what just happened. */
			await fetchWorkOrder();
		} catch (err) {
			setVerifyRequestError(err instanceof Error ? err.message : 'Could not send the verification request. Please try again.');
		} finally {
			setVerifyRequestSending(false);
		}
	};

	/**
	 * Renders a single PDF report for the work order using jspdf, then
	 * triggers a download. All content comes from the already-loaded
	 * `workOrder` state, so no extra network round-trip is needed.
	 */
	const handleGeneratePdf = () => {
		if (!workOrder) return;
		setIsGeneratingPdf(true);
		try {
			generateWorkOrderPdf(workOrder);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to generate PDF');
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	/**
	 * Handles photo deletion by calling the API and re-fetching.
	 */
	const handleDeletePhoto = async (photoId: string) => {
		if (!id) return;
		try {
			await workOrderService.deletePhoto(id, photoId);
			await fetchWorkOrder();
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to delete photo');
		}
	};

	/**
	 * Handles a captured photo from the PhotoCapture component (integrated into PhotoGrid).
	 *
	 * Returns immediately. The photo was already compressed by PhotoCapture, so
	 * all this does is hand the upload to the background queue and let the tech
	 * carry on — no spinner, no blocked UI, and several photos can be in flight
	 * at once. PhotoGrid renders the in-flight tile with a status chip, and the
	 * queue refetches the work order once the server has the photo.
	 *
	 * Offline captures go to the IndexedDB photo store (STORE_NAMES.PHOTOS),
	 * which syncService.syncPhotos() drains on reconnect. The photo type is
	 * stored by NAME rather than id: resolving the id needs the network, which
	 * is exactly what we don't have, so the resolution is deferred to sync time.
	 *
	 * @param photoType - Whether this is a 'before' or 'after' photo
	 * @param photo		- The CapturedPhoto data from the PhotoCapture component
	 */
	const handlePhotoCaptured = useCallback(async (photoType: 'before' | 'after', photo: CapturedPhoto) => {
		if (!id) return;

		/* Both branches need the seeded type name — 'Before' / 'After' are
		   created per company by the seed migration. */
		const seededName = photoType === 'before' ? 'Before' : 'After';

		if (!isOnline) {
			/* Offline: persist to IndexedDB so the capture survives a reload,
			   and let the sync service push it when connectivity returns. */
			try {
				await addPendingItem(STORE_NAMES.PHOTOS, {
					parentType: 'work-order',
					parentId: id,
					photoTypeName: seededName,
					base64: photo.base64,
					fileName: photo.fileName,
					mimeType: photo.mimeType,
					companyId: workOrder?.companyId ? String(workOrder.companyId) : null,
					caption: photo.fileName,
					capturedAt: photo.capturedAt,
				});
				alert('Photo saved on this device. It will upload when you are back on a signal.');
			} catch (err) {
				alert('Failed to save the photo for offline sync.');
			}
			return;
		}

		/* Online: enqueue and return. Everything below runs in the background —
		   including the photo-type lookup, which is a network round trip we
		   don't want the user waiting on either. */
		photoUploads.enqueue({
			previewUrl: photo.base64,
			meta: { photoType },
			upload: async () => {
				const photoTypeId = workOrder?.companyId
					? await photoTypeService.resolveByName(String(workOrder.companyId), seededName)
					: null;
				if (!photoTypeId) {
					throw new Error(`No "${seededName}" photo type is configured for this company. Ask a Super Admin to seed it in the CMS.`);
				}

				const file = await capturedPhotoToFile(photo);
				return uploadService.uploadWorkOrderPhoto(id, file, {
					photoTypeId,
					caption: photo.fileName,
				});
			},
			/* Pull the persisted record so the optimistic tile can retire. */
			onUploaded: () => fetchWorkOrder(),
		});
	}, [id, isOnline, addPendingItem, photoUploads, fetchWorkOrder, workOrder?.companyId]);

	/**
	 * Handles a signature capture from the SignatureCapture component.
	 * Uploads the signature PNG data URL to the server via the upload service.
	 * Re-fetches the work order after successful upload to reflect the change.
	 *
	 * @param signatureDataUrl - The base64 PNG data URL from the signature canvas
	 */
	const handleSignatureSave = useCallback(async (signatureDataUrl: string) => {
		if (!id) return;
		try {
			/* Upload the signature image to the work order's signature endpoint */
			await uploadService.uploadSignature('work_order', id, signatureDataUrl);
			/* Re-fetch the work order to update the signature display */
			await fetchWorkOrder();
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to save signature');
		}
	}, [id, fetchWorkOrder]);

	/* formatLabel lives in utils/formatLabel — see the import block. */

	/* Tab configuration for the navigation bar.
	 * The Photos tab only appears once at least one photo has been
	 * captured for the work order — empty WOs hide it so the user
	 * doesn't navigate into a blank tab. Once the first photo lands
	 * (via the workflow or a manual upload elsewhere), it reappears
	 * on the next render. */
	const hasAnyPhotos = (workOrder?.photos?.length ?? 0) > 0;
	/* Billing only becomes relevant once the job has been estimated — with no
	 * estimate there is nothing to bill against, so the tab stays hidden until
	 * a single-point cost or a min/max range is set. */
	const hasEstimate = !!(workOrder?.estimatedCost || (workOrder as any)?.estimateMin || (workOrder as any)?.estimateMax);

	/* ---- Hour tracking readout (work orders with "Require Hours" on) ----
	 * Derived from the eager-loaded assignees pivot, which carries each
	 * person's allocated_hours. Both key spellings are accepted because
	 * pivot payloads reach the client camelized while cached/offline
	 * snapshots may still hold the raw snake_case shape. */
	const hoursByAssignee = ((workOrder as any)?.assignees ?? [])
		.map((assignee: any) => {
			const pivot = assignee?.pivot ?? {};
			const hours = Number(pivot.allocatedHours ?? pivot.allocated_hours ?? 0);
			return {
				id: String(assignee?.id ?? ''),
				name: formatPersonName(assignee) ?? 'Unnamed',
				hours: Number.isFinite(hours) ? hours : 0,
			};
		})
		.filter((row: { hours: number }) => row.hours > 0);

	/** Total recorded across the crew — what the customer is billed from. */
	const recordedHours = hoursByAssignee.reduce((sum: number, row: { hours: number }) => sum + row.hours, 0);

	/** The bucket this work order draws from (work_orders.allocated_hours).
	 *  Null when none was entered, which is allowed on everything but T&M. */
	const bucketRaw = Number((workOrder as any)?.allocatedHours ?? NaN);
	const hoursBucket = Number.isFinite(bucketRaw) && bucketRaw > 0 ? bucketRaw : null;

	/** Contacts offered for customer approval of the hours. Narrowed to the
	 *  work order's own primary contact when it has one — that is the person
	 *  the work was performed for, and the only customer-side approver the
	 *  server accepts. Legacy rows with no linked contact fall back to the
	 *  company list, matching the server's own fallback. */
	const hoursApprovalContacts = workOrder?.contactId
		? companyContacts.filter((c) => String(c.id) === String(workOrder.contactId))
		: companyContacts;
	const tabs: { key: DetailTab; label: string; visible: boolean }[] = [
		{ key: 'details', label: 'Details', visible: true },
		{ key: 'photos', label: 'Photos', visible: hasAnyPhotos },
		{ key: 'billing', label: 'Billing', visible: canViewFinancials && hasEstimate },
	];

	/* --- Loading state --- */
	if (isLoading) {
		return (
			<div className="work-order-detail-page__loading p-24px flex items-center justify-center min-h-[400px]">
				<Spinner size="lg" label="Loading work order" />
			</div>
		);
	}

	/* --- Error state --- */
	if (error || !workOrder) {
		return (
			<div className="p-24px">
				<div className="work-order-detail-page__error bg-red-50 border border-red-200 rounded-lg p-24px text-center">
					<p className="text-14px font-sans text-red-700">{error || 'Work order not found'}</p>
					<button type="button" onClick={() => navigate('/work-orders')} className="mt-12px text-14px font-sans font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">
						Back to Work Orders
					</button>
				</div>
			</div>
		);
	}

	/* Separate photos into before and after groups for the photo grid */
	const beforePhotos = (workOrder.photos || []).filter((p: WorkOrderPhoto) => p.photoType === 'before');
	const afterPhotos = (workOrder.photos || []).filter((p: WorkOrderPhoto) => p.photoType === 'after');

	/* ============================================
	 * ACTION CLUSTER
	 * ============================================
	 * Every action on this record funnels into a single DetailActionBar. The
	 * page used to render a header button cluster AND a separate status
	 * transition bar directly beneath it, which on a phone stacked two solid
	 * orange CTAs ("Resume Workflow" and "Complete") plus a full-size red
	 * "Cancel" into a ragged wall of buttons with no clear hierarchy.
	 *
	 * One bar, one hierarchy: a single solid primary, brand-outlined supporting
	 * actions, neutral utilities, and the rare/destructive ones behind the kebab. */

	/* Status steps this user may take from here. */
	const transitions = user ? getStatusTransitions('work_order', workOrder.status, user.role) : [];
	/* Forward progress (including "Put On Hold") stays on screen; cancellation is
	   destructive, so it moves into the overflow menu rather than sitting next
	   to the primary CTA. */
	const forwardTransitions = transitions.filter((t) => t.variant !== 'danger');
	const cancelTransition = transitions.find((t) => t.variant === 'danger');

	/* Workflow re-entry — available once work is underway so the tech can pick
	   the checklist flow back up where they left it. */
	const canResumeWorkflow = workOrder.status === 'in_progress';

	/* Who a verification request would reach. Same rule the prompt renders by,
	   so the button can't offer to email someone the dialog won't name. */
	const verificationRecipients = reachableRecipients(
		workOrder.contactEmail ? [{ name: workOrder.contactName || null, email: workOrder.contactEmail }] : [],
	);

	/* "Email Contact to Verify" belongs to the review window: the work is done
	   and someone is looking at it, which is exactly when the customer should
	   be asked. Restricted to the review tier (the endpoint enforces the same),
	   because deciding the work is fit for the customer to sign off isn't the
	   submitting tech's call. The API also accepts this on a completed work
	   order — a deliberate escape hatch for a signature chased down after the
	   fact — but the button stays out of the way there. */
	const canRequestVerification = canEdit
		&& verificationRecipients.length > 0
		&& ['in_review', 'approved'].includes(workOrder.status);
	const hasAskedContact = !!workOrder.contactNotifiedAt;

	/* The forward status step takes the primary slot whenever there's no
	   workflow to resume (Start Work, Approve, Complete, Mark Invoiced, …). */
	const primaryTransition = !canResumeWorkflow ? forwardTransitions[0] : undefined;

	const primaryAction: DetailAction | null = canResumeWorkflow
		? {
			label: 'Resume Workflow',
			onClick: () => setShowWorkflow(true),
			/* Clipboard checklist icon */
			icon: <Icon size="sm"><path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" /></Icon>,
		}
		: primaryTransition
			? { label: primaryTransition.label, onClick: () => handleStatusTransition(primaryTransition.toStatus) }
			: null;

	/* Supporting actions. Forward transitions that didn't take the primary slot
	   keep brand-outline emphasis; Edit sits with them; PDF generation is a
	   neutral utility that should recede. */
	const detailActions: (DetailAction | false)[] = [
		...forwardTransitions
			.filter((t) => t !== primaryTransition)
			.map((t) => ({ key: t.toStatus, label: t.label, onClick: () => handleStatusTransition(t.toStatus), variant: 'secondary' as const })),
		/* Sits with the forward transitions rather than behind the kebab: while
		   a work order is in review, chasing the customer's signature is the
		   other thing a reviewer is here to do. */
		canRequestVerification && {
			key: 'request-verification',
			label: hasAskedContact ? 'Resend Verification Email' : 'Email Contact to Verify',
			onClick: () => { setVerifyRequestError(null); setShowVerifyPrompt(true); },
			variant: 'secondary' as const,
		},
		canEdit && { key: 'edit', label: 'Edit', onClick: () => setShowEditModal(true), variant: 'secondary' as const },
		/* Generate PDF — single-click client-side render via jspdf. The
		   internal/customer dropdown that lived here is gone intentionally: the
		   team moved off backend PDF rendering in favor of client-side jspdf,
		   which currently emits a single full-detail document. Audience
		   filtering is a follow-up item on the jspdf renderer. */
		{ key: 'pdf', label: isGeneratingPdf ? 'Generating…' : 'Generate PDF', onClick: handleGeneratePdf, variant: 'quiet' as const, isLoading: isGeneratingPdf },
	];

	/* Overflow actions. Duplicate and Create Follow-Up are both low-frequency
	   "spin off a new record" moves that don't earn a full-size button on a
	   phone, and Cancel is destructive — far too easy to fat-finger when it's a
	   red button beside the primary CTA. */
	const detailMenuItems = [
		canEdit && {
			label: 'Duplicate',
			onClick: () => navigate(`/work-orders/create?duplicate=${workOrder.id}`),
		},
		canEdit && {
			label: 'Create Follow-Up',
			onClick: () => navigate(`/work-orders/create?follow_up_from_wo=${workOrder.id}`),
		},
		cancelTransition && {
			label: cancelTransition.label,
			onClick: () => handleStatusTransition(cancelTransition.toStatus),
			variant: 'danger' as const,
		},
		canEdit && {
			label: 'Delete',
			onClick: () => requestDelete(workOrder.id, workOrder.projectName),
			variant: 'danger' as const,
		},
	];

	return (
		<PageContainer block="work-order-detail-page" dataRef="page-work-order-detail-page">
			{/* --- Back navigation link --- */}
			<BackLink to="/work-orders" label="Back to Work Orders" />

			{/* --- Verification request confirmation --- Sending is quiet by
			    nature: an email leaves for someone who isn't in the room. Say
			    plainly that it went, and to which address, until the user
			    leaves the page. */}
			{verifyRequestSentTo && (
				<Alert variant="success" className="mb-16px" onDismiss={() => setVerifyRequestSentTo(null)} dismissLabel="Dismiss confirmation">
					Verification request sent to <span className="font-semibold break-all">{verifyRequestSentTo}</span>.
				</Alert>
			)}

			{/* --- Follow-up parent banner --- Render above the page header
			    when this WO was spawned via the Create Follow-Up flow. Plain
			    inline link out to the parent so the user can step back to
			    where this revisit came from. */}
			{(workOrder.parentWorkOrder || workOrder.parentInspection) && (
				<div className="work-order-detail-page__parent-banner mb-16px px-16px py-10px bg-brand-surface border border-brand-border rounded-md text-14px font-sans text-brand-text">
					<span className="font-semibold">Follow-up of </span>
					{workOrder.parentWorkOrder ? (
						<button
							type="button"
							onClick={() => navigate(`/work-orders/${workOrder.parentWorkOrder?.id}`)}
							className="text-brand-primary hover:underline"
						>
							{`Work Order #${workOrder.parentWorkOrder.id}${(workOrder.parentWorkOrder.projectName || workOrder.parentWorkOrder.project_name) ? ` — ${workOrder.parentWorkOrder.projectName || workOrder.parentWorkOrder.project_name}` : ''}`}
						</button>
					) : workOrder.parentInspection ? (
						<button
							type="button"
							onClick={() => navigate(`/inspections/${workOrder.parentInspection?.id}`)}
							className="text-brand-primary hover:underline"
						>
							{`Inspection #${workOrder.parentInspection.id}${workOrder.parentInspection.location?.name ? ` — ${workOrder.parentInspection.location.name}` : ''}`}
						</button>
					) : null}
				</div>
			)}

			{/* --- Page header: project name, status badge, business line badge ---
			    Stay stacked through the tablet range (768–1024px) and only go
			    side-by-side at desktop. Going row at `tablet` crammed the title
			    against the 4–5 action buttons in the middle-screen zone; keeping
			    them stacked there gives the buttons a full-width row to wrap into. */}
			<div
				className="work-order-detail-page__header flex flex-col desktop:flex-row desktop:items-center desktop:justify-between gap-12px mb-24px pl-12px border-l-4 rounded-sm"
				style={{
					/* Accent strip color matches the calendar chip the user
					 * clicked through from — purple for project WOs, brand
					 * orange for standard WOs. */
					borderLeftColor:
						CATEGORY_ACCENT_COLOR[
							isProjectBusinessLine(workOrder.businessLine) ? 'project_work_order' : 'work_order'
						],
				}}
			>
				<div>
					<h1 className="page-title text-24px font-bold font-heading text-brand-heading mb-8px">
						{workOrder.projectName}
					</h1>
					<div className="flex items-center gap-8px flex-wrap">
						{/* Category badge — same atom + palette used on the calendar
							so the page reads as the same record visually. */}
						<EventTypeBadge
							type={isProjectBusinessLine(workOrder.businessLine) ? 'project_work_order' : 'work_order'}
							size="sm"
						/>
						{/* Status badge */}
						<span className={['inline-flex items-center px-10px py-2px rounded-full text-12px font-sans font-medium border', STATUS_COLORS[workOrder.status] || STATUS_COLORS.draft].join(' ')}>
							{formatLabel(workOrder.status)}
						</span>
						{/* Business line badge */}
						<span className={['inline-flex items-center px-10px py-2px rounded-full text-12px font-sans font-medium border', BUSINESS_LINE_COLORS[workOrder.businessLine] || 'bg-gray-100 text-gray-800 border-gray-300'].join(' ')}>
							{formatLabel(workOrder.businessLine)}
						</span>
						{/* Priority indicator */}
						{workOrder.priority && workOrder.priority !== 'medium' && (
							<span className={['inline-flex items-center px-10px py-2px rounded-full text-12px font-sans font-medium border', workOrder.priority === 'urgent' ? 'bg-red-100 text-red-800 border-red-300' : workOrder.priority === 'high' ? 'bg-orange-100 text-orange-800 border-orange-300' : 'bg-gray-100 text-gray-800 border-gray-300'].join(' ')}>
								{formatLabel(workOrder.priority)} Priority
							</span>
						)}
					</div>
				</div>

				{/* Every action for this work order — workflow re-entry, status
				    steps, Edit, PDF, and the overflow menu — in one responsive
				    cluster. See the ACTION CLUSTER block above for how the
				    primary/supporting/overflow split is decided. */}
				<DetailActionBar
					primaryAction={primaryAction}
					actions={detailActions}
					menuItems={detailMenuItems}
					menuAriaLabel={`Actions for work order ${workOrder.id}`}
				/>
			</div>

			{/* --- Tab navigation ---
			    overflow-x-auto + non-shrinking tabs so the strip scrolls
			    sideways on small screens instead of squashing or wrapping. */}
			<div className="work-order-detail-page__tabs border-b border-brand-border mb-24px overflow-x-auto">
				<nav className="flex gap-0px -mb-px">
					{tabs.filter((t) => t.visible).map((tab) => (
						<button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={[
							'flex-shrink-0 whitespace-nowrap px-16px py-12px text-14px font-sans font-medium border-b-2 transition-colors min-h-44px',
							activeTab === tab.key
								? 'border-brand-primary text-brand-primary'
								: 'border-transparent text-brand-text-muted hover:text-brand-primary hover:border-brand-border',
						].join(' ')}>
							{tab.label}
						</button>
					))}
				</nav>
			</div>

			{/* --- Tab content --- */}
			<div className="work-order-detail-page__panel bg-white shadow rounded-lg border border-brand-border p-24px">
				{/* === Details Tab === */}
				{activeTab === 'details' && (
					<div className="grid grid-cols-1 tablet:grid-cols-2 gap-24px">
						{/* Project Information section */}
						<div>
							<h3 className="text-18px font-heading font-semibold text-brand-heading mb-16px">
								Project Information
							</h3>
							<dl className="space-y-12px">
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Project Name</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.projectName}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Description</dt>
									<dd className="text-14px font-sans mt-2px whitespace-pre-line">{workOrder.description || '—'}</dd>
								</div>
								{workOrder.subType && (
									<div>
										<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Sub Type</dt>
										<dd className="text-14px font-sans mt-2px">{formatLabel(workOrder.subType)}</dd>
									</div>
								)}
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Scheduled Date</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.scheduledDate ? formatDate(workOrder.scheduledDate) : '—'}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Signature Required</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.signatureRequired ? 'Yes' : 'No'}</dd>
								</div>
								{/* Estimate — single-point cost or optional min/max range, with
									approval state surfaced via EstimateApprovalControl below. */}
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Estimate</dt>
									<dd className="text-14px font-sans mt-2px space-y-6px">
										{(workOrder.estimatedCost || workOrder.estimateMin || workOrder.estimateMax) ? (
											<>
												<EstimateBadge
													cost={(workOrder as any).estimatedCost}
													min={(workOrder as any).estimateMin}
													max={(workOrder as any).estimateMax}
													approved={!!(workOrder as any).estimateApprovedAt}
													size="md"
												/>
												{/* Approval workflow is only meaningful once an estimate
													exists — there is nothing to approve otherwise. */}
												<EstimateApprovalControl
													recordId={String(workOrder.id)}
													approval={{
														approvedAt: (workOrder as any).estimateApprovedAt ?? null,
														approvedByUserName: formatPersonName((workOrder as any).estimateApprovedByUser),
														approvedByContactName: formatPersonName((workOrder as any).estimateApprovedByContact),
													}}
													contacts={companyContacts}
													canApprove={canEdit}
													onChange={fetchWorkOrder}
												/>
											</>
										) : (
											<span className="text-brand-text-muted">No estimate yet</span>
										)}
									</dd>
								</div>

								{/* Hours — estimate versus actual. The bucket (allocated_hours)
									is what the office planned; the crew's per-person hours are
									what the job took, confirmed at hand-off and rolled up into
									total_hours. Shown on any work order that has either figure.
									The approval trail below applies only to hour-tracked jobs —
									it sits next to Estimate deliberately: that approves the cost
									BEFORE the work, this approves the hours AFTER it. */}
								{(workOrder.requiresHours || recordedHours > 0 || hoursBucket != null) && (
									<div>
										<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Hours</dt>
										<dd className="text-14px font-sans mt-2px space-y-6px">
											<div data-ref="page-work-order-detail-page__hours-summary">
												<span className="font-medium">{recordedHours}</span> actual
												{hoursBucket != null && (
													<span className="text-brand-text-muted"> · {hoursBucket} estimated</span>
												)}
												{/* Over-run is worth flagging but never blocks — the
													figure that matters is settled at approval. */}
												{hoursBucket != null && recordedHours > hoursBucket && (
													<span className="text-red-600"> · over by {Number((recordedHours - hoursBucket).toFixed(2))}</span>
												)}
												{hoursBucket != null && recordedHours > 0 && recordedHours < hoursBucket && (
													<span className="text-green-700"> · under by {Number((hoursBucket - recordedHours).toFixed(2))}</span>
												)}
											</div>

											{/* Who worked on it. The per-assignee split is the whole
												point of hour tracking, so it is spelled out rather
												than rolled into the total above. */}
											{hoursByAssignee.length > 0 ? (
												<ul className="text-13px text-brand-text space-y-2px">
													{hoursByAssignee.map((row: { id: string; name: string; hours: number }) => (
														<li key={row.id}>
															{row.name}
															<span className="text-brand-text-muted"> — {row.hours} hrs</span>
														</li>
													))}
												</ul>
											) : (
												<span className="text-brand-text-muted text-13px">No hours recorded yet</span>
											)}

											{workOrder.requiresHours && (
											<HoursApprovalControl
												recordId={String(workOrder.id)}
												approval={{
													submittedAt: (workOrder as any).hoursSubmittedAt ?? null,
													submittedByName: formatPersonName((workOrder as any).hoursSubmittedBy),
													approvedAt: (workOrder as any).hoursApprovedAt ?? null,
													approvedByUserName: formatPersonName((workOrder as any).hoursApprovedByUser),
													approvedByContactName: formatPersonName((workOrder as any).hoursApprovedByContact),
												}}
												contacts={hoursApprovalContacts}
												canApprove={canEdit}
												onChange={fetchWorkOrder}
											/>
											)}
										</dd>
									</div>
								)}

								{/* Assigned checklist — surfaces the source template + version, with item count.
									Change-checklist flow goes through the Edit modal (which carries the same
									dropdown), so no inline change action here. */}
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Checklist</dt>
									<dd className="text-14px font-sans mt-2px">
										{checklistInstance?.sourceChecklist ? (
											<>
												{checklistInstance.sourceChecklist.title}
												<span className="text-12px text-brand-text-muted ml-8px">
													v{checklistInstance.sourceChecklist.version} • {checklistInstance.items?.length ?? 0} items
												</span>
											</>
										) : checklistInstance ? (
											<>Custom (no template) <span className="text-12px text-brand-text-muted ml-8px">{checklistInstance.items?.length ?? 0} items</span></>
										) : (
											<span className="text-brand-text-muted">None</span>
										)}
									</dd>
								</div>
							</dl>
						</div>

						{/* Location and Contact section */}
						<div>
							<h3 className="text-18px font-heading font-semibold text-brand-heading mb-16px">
								Location & Contact
							</h3>
							<dl className="space-y-12px">
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Company</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.companyName}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Location</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.locationName}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Project Address</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.projectAddress || '—'}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Assignee</dt>
									{/* Subcontractor badge surfaces external vendors so dispatchers
									    can tell internal techs from third-party assignees at a glance. */}
									<dd className="text-14px font-sans mt-2px">
										{workOrder.assigneeName ? (
											<span className="inline-flex items-center gap-6px">
												{workOrder.assigneeName}
												{workOrder.assigneeRole === 'subcontractor' && (
													<span className="inline-flex items-center px-6px py-1px text-10px font-sans font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full border border-amber-300">Subcontractor</span>
												)}
											</span>
										) : 'Unassigned'}
									</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Contact Name</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.contactName || '—'}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Contact Phone</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.contactPhone || '—'}</dd>
								</div>
								<div>
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Contact Email</dt>
									<dd className="text-14px font-sans mt-2px">{workOrder.contactEmail || '—'}</dd>
								</div>
								{/* Whether the customer has actually been asked to verify and
								    sign. Blank on work orders that never reached the hand-off;
								    "Not requested" once they have, so a reviewer can tell the
								    difference between "waiting on them" and "nobody asked". */}
								{(workOrder.contactNotifiedAt || ['in_review', 'approved'].includes(workOrder.status)) && (
									<div>
										<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Verification Requested</dt>
										<dd className="text-14px font-sans mt-2px">
											{workOrder.contactNotifiedAt
												? formatDateTime(workOrder.contactNotifiedAt)
												: <span className="text-brand-text-muted">Not requested</span>}
										</dd>
									</div>
								)}
							</dl>
						</div>

						{/* Signature section — shown when signatureRequired is true.
							Full-width row spanning both columns in the grid.
							If a signature already exists (workOrder.signatureData), it displays
							as read-only. Otherwise shows the interactive drawing pad.
							Only editable when the user has edit permissions. */}
						{workOrder.signatureRequired && (
							<div className="tablet:col-span-2 border-t border-brand-border pt-16px">
								<h3 className="text-18px font-heading font-semibold text-brand-heading mb-16px">
									Customer Signature
								</h3>
								<SignatureCapture onSave={handleSignatureSave} existingSignature={workOrder.signatureData ?? undefined} disabled={!canEdit} label="Customer Contact Signature" />
							</div>
						)}

						{/* Timestamps section (full width) */}
						<div className="tablet:col-span-2 border-t border-brand-border pt-16px">
							<div className="flex flex-wrap gap-24px text-12px font-sans text-brand-text-muted">
								<span>Created: {formatDateTime(workOrder.createdAt)}</span>
								<span>Updated: {formatDateTime(workOrder.updatedAt)}</span>
							</div>
						</div>
					</div>
				)}

				{/* === Photos Tab === */}
				{/* Integrates PhotoCapture via PhotoGrid's onPhotoCaptured prop.
					When a photo is captured, handlePhotoCaptured uploads it (online)
					or queues it for later sync (offline). */}
				{activeTab === 'photos' && (
					<div className="space-y-24px">
						{/* Before photos grid — uses inline PhotoCapture for adding photos */}
						<PhotoGrid photos={beforePhotos} photoTypeLabel="Before Photos" editable={canEdit} onDelete={handleDeletePhoto} onPhotoCaptured={(photo) => handlePhotoCaptured('before', photo)} pendingPhotos={photoUploads.items.filter((item) => item.meta.photoType === 'before')} onRetryUpload={photoUploads.retry} onDismissUpload={photoUploads.dismiss} />

						{/* Divider between before and after sections */}
						<div className="border-t border-brand-border"></div>

						{/* After photos grid — uses inline PhotoCapture for adding photos */}
						<PhotoGrid photos={afterPhotos} photoTypeLabel="After Photos" editable={canEdit} onDelete={handleDeletePhoto} onPhotoCaptured={(photo) => handlePhotoCaptured('after', photo)} pendingPhotos={photoUploads.items.filter((item) => item.meta.photoType === 'after')} onRetryUpload={photoUploads.retry} onDismissUpload={photoUploads.dismiss} />
					</div>
				)}

				{/* === Billing Tab === */}
				{/* Only rendered when canViewFinancials is true and the job has an estimate */}
				{activeTab === 'billing' && canViewFinancials && hasEstimate && (
					<div>
						<h3 className="text-18px font-heading font-semibold text-brand-heading mb-16px">
							Billing Information
						</h3>
						<dl className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
							<div>
								<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Billing Type</dt>
								<dd className="text-14px font-sans mt-2px">{formatLabel(workOrder.billingType)}</dd>
							</div>
							<div>
								<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Billing Amount</dt>
								<dd className="text-14px font-sans mt-2px font-semibold">
									{workOrder.billingAmount != null ? formatCurrency(workOrder.billingAmount) : '—'}
								</dd>
							</div>
							<div>
								<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Billing Name</dt>
								<dd className="text-14px font-sans mt-2px">{workOrder.billingName || '—'}</dd>
							</div>
							<div>
								<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Billing Address</dt>
								<dd className="text-14px font-sans mt-2px">{workOrder.billingAddress || '—'}</dd>
							</div>
							{workOrder.billingInfo && workOrder.billingInfo.length > 0 && (
								<div className="tablet:col-span-2">
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Billing Categories</dt>
									<dd className="text-14px font-sans mt-2px">
										<div className="flex flex-wrap gap-4px">
											{workOrder.billingInfo.map((item, idx) => (
												<span key={idx} className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-brand-surface border border-brand-border">
													{item}
												</span>
											))}
										</div>
									</dd>
								</div>
							)}
							{workOrder.billingNotes && (
								<div className="tablet:col-span-2">
									<dt className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">Billing Notes</dt>
									<dd className="text-14px font-sans mt-2px whitespace-pre-line">{workOrder.billingNotes}</dd>
								</div>
							)}
						</dl>
					</div>
				)}
			</div>

			{/* === Edit Work Order Modal === */}
			{/* Uses the shared JobForm in edit mode inside an xl Modal.
				On success, closes the modal and re-fetches the work order to reflect changes. */}
			<Modal
				isOpen={showEditModal}
				onClose={requestCloseEditModal}
				title="Edit Work Order"
				size="xl"
			>
				<JobForm
					formType="work_order"
					editId={workOrder.id}
					initialData={workOrder}
					onDirtyChange={setEditFormDirty}
					onSuccess={() => {
						closeEditModal();
						fetchWorkOrder();
						fetchChecklistInstance();
					}}
				/>
			</Modal>
			{discardEditPrompt}

			{/* === Ask the on-site contact to verify + sign === */}
			{/* Reviewer-side twin of the technician's pre-submit prompt: same
				recipient list, same "know before you send" rule. Reached from
				the action bar once the work order is in review. */}
			<ConfirmModal
				isOpen={showVerifyPrompt}
				onClose={() => setShowVerifyPrompt(false)}
				onConfirm={handleRequestVerification}
				title={hasAskedContact ? 'Resend verification email' : 'Email contact to verify'}
				confirmLabel={hasAskedContact ? 'Resend Email' : 'Send Email'}
				loading={verifyRequestSending}
			>
				<RecipientList recipients={verificationRecipients} purpose="to verify and sign" emptyText="No email address is on file for the on-site contact, so nobody outside the team will be emailed." />

				{hasAskedContact && workOrder.contactNotifiedAt && (
					<p className="mt-12px text-13px font-sans text-brand-text-muted">
						{`They were last asked on ${formatDateTime(workOrder.contactNotifiedAt)}.`}
					</p>
				)}

				{verifyRequestError && (
					<p className="mt-12px text-13px font-sans text-red-600" role="alert">{verifyRequestError}</p>
				)}
			</ConfirmModal>

			{/* === Work Order Completion Workflow Overlay === */}
			{/* Full-screen 3-step workflow: Checklist -> Verify -> Sign Off.
				Renders on top of the detail page. User can close and resume later. */}
			{user && (
				<WorkOrderWorkflow
					isOpen={showWorkflow}
					onClose={() => setShowWorkflow(false)}
					workOrderId={workOrder.id}
					workOrderTitle={workOrder.projectName}
					currentUserId={user.id}
					/* Pass companyId so the per-item photo capture flow
					 * can resolve the seeded photo_type for this WO's
					 * customer company. */
					companyId={workOrder.companyId}
					/* The in-app Verify + Sign Off steps only apply when the
					 * on-site contact IS the assignee (contact.user_id ===
					 * assignee_id). Otherwise the assignee just completes the
					 * checklist and the separate contact is notified to verify. */
					contactIsAssignee={
						!!workOrder.contactUserId &&
						!!workOrder.assigneeId &&
						String(workOrder.contactUserId) === String(workOrder.assigneeId)
					}
					onSiteContactName={workOrder.contactName || null}
					/* Who the pre-submit prompt says will be emailed to verify
					 * and sign. Mirrors exactly what the server sends to —
					 * the work order's contact_email — so the tech is never
					 * shown a recipient that doesn't get the message, or vice
					 * versa. Empty when no address is on file. */
					verificationRecipients={verificationRecipients}
					/* The full record powers the workflow's Job Details panel:
					   the read-only job info a tech looks up on site, and the
					   crew + job notes they can add to from there. */
					workOrder={workOrder}
					/* The panel writes through PATCH /work-orders/{id}, so pull
					   the record back down to pick up the new crew / notes. */
					onWorkOrderUpdated={fetchWorkOrder}
					onComplete={() => {
						// Re-fetch the work order to reflect the completed status
						setShowWorkflow(false);
						fetchWorkOrder();
					}}
				/>
			)}

			{/* The "Notify users" prompt. Null until a status change would
			    actually email someone and the user is admin-tier. */}
			{notifyPrompt}
			{deleteModal}
		</PageContainer>
	);
};

export default WorkOrderDetailPage;
